/**
 * useGenerateDraftQuiz — admin/manager-only draft quiz generation (Phase Q1).
 *
 * Wraps the ONE authenticated generate-training-quiz call (server-enforced
 * manager/admin auth; the client never uses service-role) and reads back the
 * artifact's current quiz state for admin review. It NEVER approves a quiz and
 * NEVER enables the technician-facing feature — it only creates/refreshes a
 * DRAFT that stays hidden from technicians.
 *
 * Reading training_artifact_quizzes.questions (with answers) is allowed here
 * because RLS grants that SELECT to catalog managers only — a technician session
 * gets no rows.
 */
import { useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { mapQuizGenError } from '@/lib/training/quiz/quizAdminError';
import type { QuizAuthoringQuestion } from '@/lib/training/quiz/quizTypes';

export type QuizAdminStatus = 'none' | 'draft' | 'approved';

export interface DraftQuizResult {
  quizId: string | null;
  status: string | null;
  questionCount: number | null;
}

export function useGenerateDraftQuiz(artifactId: string) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<DraftQuizResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [existingStatus, setExistingStatus] = useState<QuizAdminStatus>('none');
  const [draftQuestions, setDraftQuestions] = useState<QuizAuthoringQuestion[] | null>(null);

  /** Read the artifact's current quiz state (manager-only via RLS). */
  const refresh = useCallback(async () => {
    const { data, error: readErr } = await supabase
      .from('training_artifact_quizzes')
      .select('id, status, questions')
      .eq('artifact_id', artifactId)
      .order('generated_at', { ascending: false });
    if (readErr || !data || data.length === 0) {
      setExistingStatus('none');
      setDraftQuestions(null);
      return;
    }
    const rows = data as Array<{ status: string; questions: unknown }>;
    const approved = rows.find((q) => q.status === 'approved');
    const draft = rows.find((q) => q.status === 'draft');
    setExistingStatus(approved ? 'approved' : draft ? 'draft' : 'none');
    const show = draft ?? rows[0];
    setDraftQuestions(Array.isArray(show?.questions) ? (show.questions as QuizAuthoringQuestion[]) : null);
  }, [artifactId]);

  /** The single AI call. Returns the draft result or null on error. */
  const generate = useCallback(async (): Promise<DraftQuizResult | null> => {
    setIsGenerating(true);
    setError(null);
    setResult(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('generate-training-quiz', {
        body: { artifactId },
      });
      if (fnErr) {
        const status = (fnErr as { context?: { status?: number } })?.context?.status;
        throw new Error(mapQuizGenError(status, (fnErr as Error).message));
      }
      const payload = data as { error?: string; quizId?: string; status?: string; questionCount?: number } | null;
      if (payload?.error) throw new Error(payload.error);
      const r: DraftQuizResult = {
        quizId: payload?.quizId ?? null,
        status: payload?.status ?? null,
        questionCount: payload?.questionCount ?? null,
      };
      setResult(r);
      await refresh();
      return r;
    } catch (e) {
      setError((e as Error)?.message || 'Quiz generation failed');
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [artifactId, refresh]);

  return { isGenerating, result, error, existingStatus, draftQuestions, generate, refresh };
}
